<template>
    <div class="show-detail">
        <div class="full-loading" v-if="!show">
            <loading/>
        </div>
        <section class="show-detail-header" v-if="show">
            <div class="show-detail-header-left-side">
                <show-poster :slug="show.slug" :width="150" :mobile-width="90"/>

                <div class="show-detail-info">
                    <h3>{{ show.symbol }}</h3>
                    <h1>{{ show.name }}</h1>
                    <h2>{{ show.location.name }}</h2>

                    <show-type-indicator :type="show.type"/>

                    <button
                        class="naked-button add-to-watchlist-button"
                        @click.prevent.stop="showInterestModal = true"
                    >
                        <eye-icon/>Add to My Watchlist
                    </button>
                </div>
                <div class="show-detail-meta">
                    <ul>
                        <li>
                            <seat-icon/>
                            <p>{{ show.location.seats | commaNumber }}</p>
                        </li>
                        <li>
                            <location-icon/>
                            <p>
                                <a
                                    :href="locationMapUrl"
                                    target="_blank"
                                >{{ show.location.address }} {{ show.location.city }}, {{ show.location.state }} {{ show.location.zip }}</a>
                            </p>
                        </li>
                    </ul>
                </div>

                <show-detail-important-dates/>
                <show-detail-all-time-snapshot/>
            </div>

            <show-detail-revenue-chart/>
        </section>

        <show-detail-one-week-stats v-if="show"/>

        <section class="show-detail-grosses-table" v-if="show">
            <grosses-table
                :table-data="grosses"
                :table-headers="headers"
                :table-sortables="sortables"
                :sort-key="currentSortKey"
                :sort-direction="currentSortDirection"
                :sortable="false"
                @sort="onSort"
            />

            <loading v-if="page !== last_page"/>
        </section>

        <show-news
            v-if="show && show.broadway_news_category_id"
            :category-id="show.broadway_news_category_id"
        />

        <interest-form
            feature="add_show_to_watchlist"
            :meta="{ show: show.slug }"
            v-if="showInterestModal"
            @closed="showInterestModal = false"
        />
    </div>
</template>

<script>
import GrossesTable from 'src/components/GrossesTable';
import EyeIcon from 'src/components/icons/Eye';
import LocationIcon from 'src/components/icons/Location';
import SeatIcon from 'src/components/icons/Seat';
import ShowDetailAllTimeSnapshot from 'src/components/ShowDetailAllTimeSnapshot';
import ShowDetailImportantDates from 'src/components/ShowDetailImportantDates';
import ShowDetailOneWeekStats from 'src/components/ShowDetailOneWeekStats';
import ShowDetailRevenueChart from 'src/components/ShowDetailRevenueChart';
import ShowNews from 'src/components/ShowNews';
import ShowPoster from 'src/components/ShowPoster';
import ShowTypeIndicator from 'src/components/ShowTypeIndicator';
import headers from 'src/config/grosses-table-headers';
import { getNewSortKeyAndDirection } from 'src/util/helpers';

export default {
    data() {
        return {
            sortKey: 'week_end',
            sortDirection: 'desc',
            page: 1,
            last_page: null,
            headers: [
                'week_end',
                'week_number',
                'gross',
                'gross_potential_percentage',
                'attendance',
                'capacity_percentage',
                'ticket_average',
                'ticket_min',
                'ticket_max',
                'ticket_top',
                'performances',
            ],
            sortables: [
                'week_end',
                'week_number',
                'gross',
                'gross_potential_percentage',
                'attendance',
                'capacity_percentage',
                'ticket_average',
                'ticket_min',
                'ticket_max',
                'ticket_top',
                'performances',
            ],
            showInterestModal: false,
        };
    },
    metaInfo() {
        if (!this.show) {
            return {};
        }

        return {
            title: `${this.show.name} | ${
                window.broadwayBusiness.config.titleBase
            }`,
        };
    },
    components: {
        EyeIcon,
        GrossesTable,
        LocationIcon,
        SeatIcon,
        ShowDetailAllTimeSnapshot,
        ShowDetailImportantDates,
        ShowDetailOneWeekStats,
        ShowDetailRevenueChart,
        ShowNews,
        ShowPoster,
        ShowTypeIndicator,
    },
    created() {
        this.$store
            .dispatch('shows/fetchShow', {
                slug: this.$route.params.show,
                force: true,
            })
            .catch(err => {
                if (err.response.status === 404) {
                    this.$router.push({ name: 'historical' });
                }
            });
        this.$store.dispatch('shows/fetchStats', this.$route.params.show);

        this.fetchGrossesPage();
    },
    beforeDestroy() {
        this.destroyLoadingObserver();
    },
    computed: {
        show() {
            return this.$store.getters['shows/show'](this.$route.params.show);
        },
        grosses() {
            if (!this.show.id) {
                return [];
            }

            return this.$store.getters['grosses/forShow'](this.show.id);
        },
        aggregate() {
            return this.$store.getters['shows/aggregate'](this.show.slug);
        },
        locationMapUrl() {
            return `https://www.google.com/maps/place/${this.show.location.name.replace(
                / /g,
                '+',
            )}/@${this.show.location.latitude},${
                this.show.location.longitude
            },17z`;
        },
        currentSortKey() {
            return this.$store.state.shows.sortKey;
        },
        currentSortDirection() {
            return this.$store.state.shows.sortDirection;
        },
    },
    methods: {
        fetchGrossesPage(clearShow = false) {
            return this.$store
                .dispatch('grosses/fetchForShow', {
                    slug: this.$route.params.show,
                    sortKey: this.sortKey,
                    sortDirection: this.sortDirection,
                    page: this.page,
                    clearShow,
                })
                .then(result => {
                    this.createLoadingObserver();
                    this.last_page = result.last_page;

                    if (!this.$grossesTable) {
                        this.$grossesTable = this.$el.querySelector(
                            '.show-detail-grosses-table',
                        );
                    }
                });
        },
        destroyLoadingObserver() {
            if (this.loadingObserver) {
                this.loadingObserver.disconnect();
            }
        },
        createLoadingObserver() {
            this.destroyLoadingObserver();

            const $el = this.$el.querySelector(
                `.grosses-table .row:nth-child(${this.grosses.length - 9})`,
            );

            if (!$el) {
                setTimeout(this.createLoadingObserver, 100);
                return;
            }

            const callback = (entries, observer) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        if (this.page !== this.last_page) {
                            this.page = this.page + 1;
                            this.fetchGrossesPage();
                        }

                        this.destroyLoadingObserver();
                    }
                });
            };

            this.loadingObserver = new IntersectionObserver(callback, {
                threshold: 0.0,
            });

            this.loadingObserver.observe($el);
        },
        onSort({ newKey }) {
            const { key, direction } = getNewSortKeyAndDirection(
                newKey,
                this.sortKey,
                this.sortDirection,
            );

            this.page = 1;
            this.sortDirection = direction;
            this.sortKey = key;

            this.fetchGrossesPage(true);
        },
    },
};
</script>
